/** @jsxRuntime classic */
/** @jsx createElement */
import {createElement} from "@opendaw/lib-jsx"
import {isDefined, tryCatch} from "@opendaw/lib-std"
import {createSonnWordmark, createSonnGlyph} from "./SonnBrand.js"

export interface ThinkingStep {
    name: string
    status: "pending" | "done" | "failed"
    error?: string
    args?: string    // raw JSON string from tool_call_started
    result?: string  // formatted summary from tool_call_succeeded
    reasoning?: string  // human-readable description from the LLM
}

export interface RecipeStep {
    id: string
    text: string
    value?: string
    done: boolean
}

export interface ChatMessage {
    id: string
    role: "user" | "assistant" | "system"
    text: string
    ts: number
    thinking?: ReadonlyArray<ThinkingStep>
    recipe?: ReadonlyArray<RecipeStep>
    isStreaming?: boolean
}

export interface ChatSidebarProps {
    messages: ReadonlyArray<ChatMessage>
    onSend: (text: string) => void
    isThinking?: boolean
    liveThinking?: ReadonlyArray<ThinkingStep>
    sessionId?: string
    hasProject?: boolean
    className?: string
}

const PRESETS = ['mix vocal forward', 'master for spotify', 'tame the snare', 'widen the chorus', '+ preset'] as const

const summarizeStep = (step: ThinkingStep): string => {
    if (isDefined(step.reasoning)) return step.reasoning
    const base = step.name.replace(/_/g, ' ')
    const argsStr = step.args
    if (!isDefined(argsStr)) return base
    const parsed = tryCatch(() => JSON.parse(argsStr) as Record<string, unknown>)
    if (parsed.status !== 'success') return base
    const a = parsed.value
    const parts: string[] = []
    const trackName = a['track_name']
    if (typeof trackName === 'string') parts.push(trackName)
    const volDb = a['volume_db']
    if (typeof volDb === 'number') parts.push(`${volDb > 0 ? '+' : ''}${volDb.toFixed(1)} dB`)
    const gainDb = a['gain_db']
    if (typeof gainDb === 'number') parts.push(`${gainDb > 0 ? '+' : ''}${gainDb.toFixed(1)} dB`)
    const freqHz = a['frequency_hz']
    if (typeof freqHz === 'number') parts.push(`${freqHz}Hz`)
    const pan = a['pan']
    if (typeof pan === 'number') parts.push(`pan ${pan.toFixed(2)}`)
    return parts.length > 0 ? `${base} · ${parts.join(', ')}` : base
}

const renderThinking = (steps: ReadonlyArray<ThinkingStep>, isLive: boolean): HTMLElement => {
    const doneCount = steps.filter(step => step.status === 'done').length
    const pendingCount = steps.filter(step => step.status === 'pending').length
    const label = pendingCount > 0
        ? `sonn · running (${doneCount}/${steps.length})`
        : `${steps.length} tool call${steps.length !== 1 ? 's' : ''}`
    const stepNodes = steps.map(step => {
        const pip = step.status === 'done' ? '✓' : step.status === 'failed' ? '✗' : '◦'
        return (
            <div className={`sonn-chat__tool sonn-chat__tool--${step.status}`}>
                <span className="sonn-chat__tool-pip">{pip}</span>
                <span className="sonn-chat__tool-label">{summarizeStep(step)}</span>
                {isDefined(step.error) ? <span className="sonn-chat__tool-err">{step.error}</span> : null}
            </div>
        )
    })
    const details: HTMLDetailsElement = (
        <details className="sonn-chat__tools">
            <summary className="sonn-chat__tools-summary">{label}</summary>
            {stepNodes}
        </details>
    )
    if (isLive) details.open = true
    return details
}

const renderRecipe = (steps: ReadonlyArray<RecipeStep>): HTMLElement => {
    const stepNodes = steps.map((step, index) => {
        const isDone = step.done
        const pip: HTMLSpanElement = (
            <span className="sonn-chat__recipe-pip">{isDone ? '✓' : String(index + 1)}</span>
        )
        const row: HTMLDivElement = (
            <div className={`sonn-chat__recipe-step sonn-chat__recipe-step--${isDone ? 'done' : 'pending'}`}>
                {pip}
                <span>{step.text}</span>
                {isDefined(step.value) ? <span className="sonn-chat__recipe-val">{step.value}</span> : null}
            </div>
        )
        return row
    })
    const doneCount = steps.filter(step => step.done).length
    return (
        <div className="sonn-chat__recipe">
            <div className="sonn-chat__recipe-header">{`Recipe · ${steps.length} moves · ${doneCount} done`}</div>
            {stepNodes}
        </div>
    )
}

export const ChatSidebar = (props: ChatSidebarProps): HTMLElement => {
    const wordmarkEl = createSonnWordmark()

    const header: HTMLElement = (
        <header className="sonn-chat__header">
            <div className="sonn-chat__header-brand">
                <div className="sonn-chat__header-left">
                    <span className="sonn-chat__header-dot" aria-hidden="true"/>
                    {wordmarkEl}
                </div>
                <div className="sonn-chat__header-icons">
                    <span title="Command palette">⌘K</span>
                    <span title="Options">···</span>
                </div>
            </div>
        </header>
    )

    if (props.hasProject !== true) {
        const glyph = createSonnGlyph(40)
        return (
            <aside className="sonn-chat" role="complementary" aria-label="Sonn AI chat">
                {header}
                <div className="sonn-chat__idle">
                    <div className="sonn-chat__idle-glyph">{glyph}</div>
                    <div className="sonn-chat__idle-title">Your AI mix engineer</div>
                    <div className="sonn-chat__idle-body">
                        Open or create a project in the studio to activate Sonn.
                    </div>
                    <div className="sonn-chat__idle-hint">no active session</div>
                </div>
            </aside>
        )
    }

    const textarea: HTMLTextAreaElement = (
        <textarea
            className="sonn-chat__textarea"
            rows={1}
            placeholder="Ask Sonn to mix…"
            aria-label="Message input"
        />
    )

    const submit = () => {
        const value = textarea.value.trim()
        if (value.length === 0) { return }
        props.onSend(value)
        textarea.value = ""
        textarea.style.height = "auto"
    }

    textarea.onkeydown = (event: KeyboardEvent) => {
        if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault()
            submit()
        }
    }
    textarea.oninput = () => {
        textarea.style.height = "auto"
        textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`
    }

    const sendBtn: HTMLButtonElement = (
        <button className="sonn-chat__send" type="button" aria-label="Send message">Send</button>
    )
    sendBtn.onclick = submit

    const messageNodes = props.messages.map(message => {
        const isUser = message.role === "user"
        const isAgent = message.role === "assistant"
        const role = isUser ? "user" : "agent"

        const eyebrow: HTMLDivElement = (
            <div className="sonn-chat__msg-eyebrow">
                <span>{isUser ? 'you' : 'sonn'}</span>
            </div>
        )

        const bodyEl: HTMLDivElement = (
            <div className="sonn-chat__msg-body">
                {message.text}
                {message.isStreaming === true ? <span className="sonn-chat__cursor" aria-hidden="true"/> : null}
            </div>
        )

        const parts: Array<HTMLElement | null> = [eyebrow, bodyEl]

        if (isAgent) {
            const isStreaming = message.id === "__streaming__"
            const thinkingSteps = isStreaming ? props.liveThinking : message.thinking
            if (isDefined(thinkingSteps) && thinkingSteps.length > 0) {
                parts.push(renderThinking(thinkingSteps, isStreaming))
            }
        }

        if (isAgent && message.recipe && message.recipe.length > 0) {
            parts.push(renderRecipe(message.recipe))
        }

        return (
            <div className={`sonn-chat__msg sonn-chat__msg--${role}`} data-id={message.id} role="listitem">
                {parts.filter((part): part is HTMLElement => part !== null)}
            </div>
        )
    })

    const liveSteps = props.liveThinking
    const thinkingNode: HTMLElement | null = props.isThinking === true
        ? (
            <div className="sonn-chat__msg sonn-chat__msg--agent" aria-live="polite">
                <div className="sonn-chat__msg-eyebrow">
                    <span>sonn</span>
                    <span className="sonn-chat__msg-status sonn-chat__msg-status--running">● running</span>
                </div>
                {isDefined(liveSteps) && liveSteps.length > 0
                    ? renderThinking(liveSteps, true)
                    : <div className="sonn-chat__msg-body" style="color:var(--text-faint)">thinking…</div>}
            </div>
        )
        : null

    const presetNodes = PRESETS.map((preset, index) => {
        const isAdd = index === PRESETS.length - 1
        const btn: HTMLButtonElement = (
            <button className={`sonn-chat__preset${isAdd ? ' sonn-chat__preset--add' : ''}`} type="button">
                {preset}
            </button>
        )
        if (!isAdd) {
            btn.onclick = () => {
                textarea.value = preset
                textarea.focus()
            }
        }
        return btn
    })

    const sessionId = props.sessionId ?? 'default'

    return (
        <aside className="sonn-chat" role="complementary" aria-label="Sonn AI chat">
            {header}

            <div className="sonn-chat__session">
                <span>{`SESSION · ${sessionId}`}</span>
                <span className="sonn-chat__live">
                    <span className="sonn-chat__live-dot" aria-hidden="true"/>
                    live
                </span>
            </div>

            <div className="sonn-chat__feed" role="log" aria-live="polite" aria-label="Conversation">
                {messageNodes}
                {thinkingNode}
            </div>

            <div className="sonn-chat__presets" role="toolbar" aria-label="Quick prompts">
                {presetNodes}
            </div>

            <div className="sonn-chat__input-area">
                <div className="sonn-chat__input-wrap">
                    {textarea}
                    <div className="sonn-chat__input-row">
                        <div className="sonn-chat__hints" aria-hidden="true">
                            <span className="sonn-chat__hint">@track</span>
                            <span className="sonn-chat__hint">/ref</span>
                            <span className="sonn-chat__hint">⏎ run</span>
                        </div>
                        {sendBtn}
                    </div>
                </div>
                <div className="sonn-chat__disclaimer" aria-hidden="true">
                    AGENT MAY MOVE FADERS, EQ, AND PLUGINS. NOTHING IS DESTRUCTIVE.
                </div>
            </div>
        </aside>
    )
}
